import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const STATUS_STYLES = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const OPS_STYLES = {
    ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
    missing: "bg-rose-50 text-rose-700 border-rose-200",
    optional: "bg-slate-50 text-slate-600 border-slate-200",
};

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
            {status}
        </span>
    );
}

function OpsBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${OPS_STYLES[status] || OPS_STYLES.optional}`}>
            {status}
        </span>
    );
}

export default function AdminPrivacy() {
    const [requests, setRequests] = useState([]);
    const [retention, setRetention] = useState({ summary: {}, items: [] });
    const [deliveries, setDeliveries] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [review, setReview] = useState({ status: "approved", admin_response: "" });
    const [selectedRetention, setSelectedRetention] = useState(null);
    const [retentionAction, setRetentionAction] = useState({
        action: "retain",
        retention_until: "",
        retention_reason: "",
    });
    const [policy, setPolicy] = useState({
        case_records: 2190,
        private_documents: 2190,
        case_evidence: 2190,
        estate_kb: 2555,
        audit_logs: 2555,
    });
    const [digestSchedule, setDigestSchedule] = useState({
        agency_digest_schedule_enabled: false,
        agency_digest_schedule_frequency: "weekly",
        agency_digest_last_queued_at: null,
    });
    const [controller, setController] = useState({
        controller_type: "rmc",
        controller_organisation_name: "",
        controller_contact: "",
    });
    const [controllerRecord, setControllerRecord] = useState({ recorded_by: null, recorded_at: null });

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.status = filter;
            const [res, retentionRes, settingsRes, deliveriesRes, readinessRes] = await Promise.all([
                api.get("/admin/privacy-requests", { params }),
                api.get("/admin/retention-reviews"),
                api.get("/estate/settings"),
                api.get("/admin/notification-deliveries"),
                api.get("/admin/deployment-readiness"),
            ]);
            setRequests(res.data || []);
            setRetention(retentionRes.data || { summary: {}, items: [] });
            setPolicy(settingsRes.data?.retention_policy_days || retentionRes.data?.policy || policy);
            setDigestSchedule({
                agency_digest_schedule_enabled: Boolean(settingsRes.data?.agency_digest_schedule_enabled),
                agency_digest_schedule_frequency: settingsRes.data?.agency_digest_schedule_frequency || "weekly",
                agency_digest_last_queued_at: settingsRes.data?.agency_digest_last_queued_at || null,
            });
            setController({
                controller_type: settingsRes.data?.controller_type || "rmc",
                controller_organisation_name: settingsRes.data?.controller_organisation_name || "",
                controller_contact: settingsRes.data?.controller_contact || "",
            });
            setControllerRecord({
                recorded_by: settingsRes.data?.controller_recorded_by || null,
                recorded_at: settingsRes.data?.controller_recorded_at || null,
            });
            setDeliveries(deliveriesRes.data || []);
            setReadiness(readinessRes.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load privacy requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const openReview = (req) => {
        setSelected(req);
        setReview({ status: req.request_type === "correction" ? "completed" : "approved", admin_response: "" });
    };

    const submitReview = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/privacy-requests/${selected.id}/review`, review);
            toast.success("Privacy request reviewed");
            setSelected(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not review request");
        }
    };

    const openRetention = (item) => {
        setSelectedRetention(item);
        setRetentionAction({
            action: "retain",
            retention_until: item.retention_until || "",
            retention_reason: item.retention_reason || "",
        });
    };

    const submitRetention = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/retention-reviews/action", {
                resource_type: selectedRetention.resource_type,
                resource_id: selectedRetention.resource_id,
                ...retentionAction,
            });
            toast.success("Retention review saved");
            setSelectedRetention(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save retention review");
        }
    };

    const savePolicy = async (e) => {
        e.preventDefault();
        try {
            const retention_policy_days = Object.fromEntries(
                Object.entries(policy).map(([key, value]) => [key, Number(value)])
            );
            await api.put("/estate/settings", { retention_policy_days });
            toast.success("Retention policy updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update retention policy");
        }
    };

    const saveController = async (e) => {
        e.preventDefault();
        try {
            // The backend clears any stale agency name/contact itself when switching back
            // to "rmc" (controller.py) — the API filters out null fields entirely, so there
            // is no point sending them; only include organisation_name/contact when they
            // are actually meaningful (controller_type === "agency").
            const body = { controller_type: controller.controller_type };
            if (controller.controller_type === "agency") {
                body.controller_organisation_name = controller.controller_organisation_name;
                body.controller_contact = controller.controller_contact;
            }
            await api.put("/estate/settings", body);
            toast.success("Data controller designation updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update the controller designation");
        }
    };

    const saveDigestSchedule = async (e) => {
        e.preventDefault();
        try {
            await api.put("/estate/settings", {
                agency_digest_schedule_enabled: digestSchedule.agency_digest_schedule_enabled,
                agency_digest_schedule_frequency: digestSchedule.agency_digest_schedule_frequency,
            });
            toast.success("Agency digest schedule updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update agency digest schedule");
        }
    };

    const queueDeliveries = async () => {
        try {
            const res = await api.post("/admin/notification-deliveries/queue");
            toast.success(`Queued ${res.data?.queued || 0} external notification deliveries`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not queue deliveries");
        }
    };

    const processDeliveries = async () => {
        try {
            const res = await api.post("/admin/notification-deliveries/process");
            toast.success(`Processed ${res.data?.processed || 0}: sent ${res.data?.sent || 0}, failed ${res.data?.failed || 0}, pending ${res.data?.pending_provider || 0}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not process deliveries");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-privacy-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Privacy requests</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Review resident deletion, restriction, and correction requests.
                    </p>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-52" data-testid="privacy-admin-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-privacy-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Resident</th>
                            <th className="table-header-cell">Type</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Details</th>
                            <th className="table-header-cell">Created</th>
                            <th className="table-header-cell"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="table-cell-tight text-slate-500">Loading...</td></tr>}
                        {!loading && requests.length === 0 && <tr><td colSpan={6} className="table-cell-tight text-slate-500">No privacy requests.</td></tr>}
                        {requests.map((req) => (
                            <tr key={req.id} className="border-b border-slate-100" data-testid={`admin-privacy-request-${req.id}`}>
                                <td className="table-cell-tight text-slate-900">{req.user_email}</td>
                                <td className="table-cell-tight text-slate-700 capitalize">{req.request_type}</td>
                                <td className="table-cell-tight"><StatusBadge status={req.status} /></td>
                                <td className="table-cell-tight text-slate-700 max-w-sm">{req.details}</td>
                                <td className="table-cell-tight text-slate-500">{new Date(req.created_at).toLocaleDateString()}</td>
                                <td className="table-cell-tight">
                                    <Button variant="outline" size="sm" onClick={() => openReview(req)} data-testid={`review-privacy-${req.id}`}>
                                        Review
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="admin-retention-section">
                <div className="px-4 py-3 border-b border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Retention reviews</div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-sm">
                        <div><span className="text-slate-500">Total</span><div className="font-semibold text-slate-900">{retention.summary?.total || 0}</div></div>
                        <div><span className="text-slate-500">Overdue</span><div className="font-semibold text-slate-900">{retention.summary?.overdue || 0}</div></div>
                        <div><span className="text-slate-500">No policy</span><div className="font-semibold text-slate-900">{retention.summary?.needs_policy || 0}</div></div>
                        <div><span className="text-slate-500">Cases</span><div className="font-semibold text-slate-900">{retention.summary?.cases || 0}</div></div>
                        <div><span className="text-slate-500">Documents</span><div className="font-semibold text-slate-900">{retention.summary?.documents || 0}</div></div>
                    </div>
                </div>
                <table className="w-full text-sm" data-testid="admin-retention-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Record</th>
                            <th className="table-header-cell">Reason</th>
                            <th className="table-header-cell">Retention until</th>
                            <th className="table-header-cell">Visibility</th>
                            <th className="table-header-cell">Created</th>
                            <th className="table-header-cell"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && (retention.items || []).length === 0 && <tr><td colSpan={6} className="table-cell-tight text-slate-500">No retention reviews due.</td></tr>}
                        {(retention.items || []).map((item) => (
                            <tr key={`${item.resource_type}-${item.resource_id}`} className="border-b border-slate-100" data-testid={`admin-retention-${item.resource_type}-${item.resource_id}`}>
                                <td className="table-cell-tight">
                                    <div className="font-medium text-slate-900">{item.title || item.resource_id}</div>
                                    <div className="text-xs text-slate-500">{item.resource_type} · {item.status}</div>
                                </td>
                                <td className="table-cell-tight text-slate-700">{item.reason}</td>
                                <td className="table-cell-tight text-slate-700">{item.retention_until || "-"}</td>
                                <td className="table-cell-tight text-slate-700">{item.visibility || item.scope || "-"}</td>
                                <td className="table-cell-tight text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleDateString() : "-"}</td>
                                <td className="table-cell-tight">
                                    <Button variant="outline" size="sm" onClick={() => openRetention(item)} data-testid={`review-retention-${item.resource_type}-${item.resource_id}`}>
                                        Review
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="controller-section">
                <div className="text-xs uppercase tracking-widest text-slate-500">Data controller</div>
                <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                    Who is legally responsible for residents' data under UK GDPR — shown on your
                    estate's privacy notice and to residents on their Privacy page. By default this
                    is your management company. Only change this if your management company has
                    formally designated a managing agency to act as controller on its behalf — this
                    records that designation, it does not give the agency any access to this
                    product.
                </p>
                <form onSubmit={saveController} className="mt-4 space-y-3">
                    <div className="max-w-xs">
                        <Label htmlFor="controller-type">Controller</Label>
                        <Select
                            value={controller.controller_type}
                            onValueChange={(value) => setController({ ...controller, controller_type: value })}
                        >
                            <SelectTrigger id="controller-type" data-testid="controller-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="rmc">Our management company</SelectItem>
                                <SelectItem value="agency">A managing agency, on our behalf</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {controller.controller_type === "agency" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="controller-name">Agency name</Label>
                                <input
                                    id="controller-name"
                                    value={controller.controller_organisation_name}
                                    onChange={(e) => setController({ ...controller, controller_organisation_name: e.target.value })}
                                    required
                                    placeholder="e.g. Fairhurst Property Management"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                    data-testid="controller-organisation-name-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="controller-contact">Agency contact (for data protection queries)</Label>
                                <input
                                    id="controller-contact"
                                    value={controller.controller_contact}
                                    onChange={(e) => setController({ ...controller, controller_contact: e.target.value })}
                                    placeholder="dpo@agency.example"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                    data-testid="controller-contact-input"
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-3">
                        <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-controller-button">
                            Save
                        </Button>
                        {controllerRecord.recorded_at && (
                            <span className="text-xs text-slate-500" data-testid="controller-recorded-at">
                                Last recorded {new Date(controllerRecord.recorded_at).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                </form>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="retention-policy-section">
                <div className="text-xs uppercase tracking-widest text-slate-500">Retention policy periods</div>
                <p className="text-sm text-slate-500 mt-1">
                    Default review periods are used when an individual case or document does not have a specific retention date.
                </p>
                <form onSubmit={savePolicy} className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4 items-end">
                    {[
                        ["case_records", "Case records"],
                        ["private_documents", "Private docs"],
                        ["case_evidence", "Case evidence"],
                        ["estate_kb", "Estate KB"],
                        ["audit_logs", "Audit logs"],
                    ].map(([key, label]) => (
                        <div key={key} className="space-y-1.5">
                            <Label>{label}</Label>
                            <input
                                type="number"
                                min="30"
                                max="36500"
                                value={policy[key] || ""}
                                onChange={(e) => setPolicy({ ...policy, [key]: e.target.value })}
                                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                data-testid={`retention-policy-${key}`}
                            />
                        </div>
                    ))}
                    <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-retention-policy-button">
                        Save policy
                    </Button>
                </form>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="agency-digest-schedule-section">
                <div className="text-xs uppercase tracking-widest text-slate-500">Agency digest schedule</div>
                <p className="text-sm text-slate-500 mt-1">
                    A hosted cron can call <span className="font-mono">/api/cron/agency-digest</span> to queue the digest when this schedule is due.
                </p>
                <form onSubmit={saveDigestSchedule} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 items-end">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={digestSchedule.agency_digest_schedule_enabled}
                            onChange={(e) => setDigestSchedule({ ...digestSchedule, agency_digest_schedule_enabled: e.target.checked })}
                            data-testid="agency-digest-schedule-enabled"
                        />
                        Enable scheduled digest queueing
                    </label>
                    <div className="space-y-1.5">
                        <Label>Frequency</Label>
                        <Select
                            value={digestSchedule.agency_digest_schedule_frequency}
                            onValueChange={(v) => setDigestSchedule({ ...digestSchedule, agency_digest_schedule_frequency: v })}
                        >
                            <SelectTrigger data-testid="agency-digest-schedule-frequency"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-agency-digest-schedule-button">
                        Save schedule
                    </Button>
                </form>
                <div className="text-xs text-slate-500 mt-3" data-testid="agency-digest-last-queued">
                    Last queued: {digestSchedule.agency_digest_last_queued_at ? new Date(digestSchedule.agency_digest_last_queued_at).toLocaleString() : "Never"}
                </div>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="deployment-readiness-section">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Production readiness</div>
                        <p className="text-sm text-slate-500 mt-1">
                            Provider, cron, storage, and OCR checks for deployment. No secrets are displayed.
                        </p>
                    </div>
                    <div className="text-sm">
                        {readiness?.ready ? (
                            <span className="font-semibold text-emerald-700" data-testid="deployment-readiness-ready">Ready</span>
                        ) : (
                            <span className="font-semibold text-rose-700" data-testid="deployment-readiness-blocked">
                                {readiness?.blocking_count || 0} required missing
                            </span>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 text-sm">
                    <div>
                        <span className="text-slate-500">Environment</span>
                        <div className="font-semibold text-slate-900" data-testid="deployment-environment">{readiness?.environment || "-"}</div>
                    </div>
                    <div>
                        <span className="text-slate-500">Required blockers</span>
                        <div className="font-semibold text-slate-900" data-testid="deployment-blocking-count">{readiness?.blocking_count ?? "-"}</div>
                    </div>
                    <div>
                        <span className="text-slate-500">Optional missing</span>
                        <div className="font-semibold text-slate-900" data-testid="deployment-optional-count">{readiness?.optional_missing_count ?? "-"}</div>
                    </div>
                </div>
                <table className="w-full text-sm" data-testid="deployment-readiness-table">
                    <thead>
                        <tr className="border-t border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Check</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Required</th>
                            <th className="table-header-cell">Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(readiness?.checks || []).map((check) => (
                            <tr key={check.name} className="border-b border-slate-100" data-testid={`deployment-check-${check.name.toLowerCase().replaceAll(" ", "-")}`}>
                                <td className="table-cell-tight font-medium text-slate-900">{check.name}</td>
                                <td className="table-cell-tight"><OpsBadge status={check.status} /></td>
                                <td className="table-cell-tight text-slate-700">{check.required ? "Yes" : "Optional"}</td>
                                <td className="table-cell-tight text-slate-500 max-w-lg">{check.detail}</td>
                            </tr>
                        ))}
                        {!readiness?.checks?.length && (
                            <tr><td colSpan={4} className="table-cell-tight text-slate-500">Readiness checks unavailable.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="notification-delivery-section">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">External notification delivery queue</div>
                        <p className="text-sm text-slate-500 mt-1">Email/WhatsApp deliveries are staged here until provider integration is configured.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button type="button" variant="outline" onClick={queueDeliveries} data-testid="queue-notification-deliveries-button">
                            Queue current reminders
                        </Button>
                        <Button type="button" className="bg-slate-700 hover:bg-slate-800 text-white" onClick={processDeliveries} data-testid="process-notification-deliveries-button">
                            Process queue
                        </Button>
                    </div>
                </div>
                <table className="w-full text-sm" data-testid="notification-delivery-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Channel</th>
                            <th className="table-header-cell">Recipient</th>
                            <th className="table-header-cell">Title</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Reason</th>
                            <th className="table-header-cell">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {deliveries.length === 0 && <tr><td colSpan={6} className="table-cell-tight text-slate-500">No external deliveries queued.</td></tr>}
                        {deliveries.map((d) => (
                            <tr key={d.id} className="border-b border-slate-100" data-testid={`notification-delivery-${d.id}`}>
                                <td className="table-cell-tight text-slate-700">{d.channel}</td>
                                <td className="table-cell-tight text-slate-700">{d.recipient}</td>
                                <td className="table-cell-tight text-slate-900">{d.title}</td>
                                <td className="table-cell-tight text-slate-700">{d.status}</td>
                                <td className="table-cell-tight text-slate-500 max-w-sm">{d.provider_response || d.reason || "-"}</td>
                                <td className="table-cell-tight text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleString() : "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
                <DialogContent data-testid="privacy-review-dialog">
                    <DialogHeader>
                        <DialogTitle>Review privacy request</DialogTitle>
                        <DialogDescription>
                            Approving deletion will soft-delete the resident's private documents and deactivate/anonymise their account while preserving audit records.
                        </DialogDescription>
                    </DialogHeader>
                    {selected && (
                        <form onSubmit={submitReview} className="space-y-4">
                            <div className="border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                                <div><strong>Resident:</strong> {selected.user_email}</div>
                                <div><strong>Type:</strong> {selected.request_type}</div>
                                <div className="mt-2 whitespace-pre-wrap">{selected.details}</div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Decision</Label>
                                <Select value={review.status} onValueChange={(v) => setReview({ ...review, status: v })}>
                                    <SelectTrigger data-testid="privacy-review-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="approved">Approve</SelectItem>
                                        <SelectItem value="rejected">Reject</SelectItem>
                                        <SelectItem value="completed">Mark completed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Admin response</Label>
                                <Textarea
                                    rows={4}
                                    value={review.admin_response}
                                    onChange={(e) => setReview({ ...review, admin_response: e.target.value })}
                                    required
                                    data-testid="privacy-review-response-input"
                                />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="privacy-review-submit-button">
                                    Save decision
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!selectedRetention} onOpenChange={(open) => !open && setSelectedRetention(null)}>
                <DialogContent data-testid="retention-review-dialog">
                    <DialogHeader>
                        <DialogTitle>Review retention</DialogTitle>
                        <DialogDescription>
                            Record a retention decision with a reason. Document deletion is a soft delete; case deletion is not offered here to preserve audit history.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedRetention && (
                        <form onSubmit={submitRetention} className="space-y-4">
                            <div className="border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                                <div><strong>Record:</strong> {selectedRetention.title}</div>
                                <div><strong>Type:</strong> {selectedRetention.resource_type}</div>
                                <div><strong>Reason due:</strong> {selectedRetention.reason}</div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Action</Label>
                                <Select value={retentionAction.action} onValueChange={(v) => setRetentionAction({ ...retentionAction, action: v })}>
                                    <SelectTrigger data-testid="retention-action-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="retain">Retain with review date</SelectItem>
                                        <SelectItem value="restrict_processing">Restrict processing</SelectItem>
                                        {selectedRetention.resource_type === "document" && <SelectItem value="delete_document">Soft-delete document</SelectItem>}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Retention until</Label>
                                <input
                                    type="date"
                                    value={retentionAction.retention_until}
                                    onChange={(e) => setRetentionAction({ ...retentionAction, retention_until: e.target.value })}
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                                    data-testid="retention-until-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Reason</Label>
                                <Textarea
                                    rows={4}
                                    value={retentionAction.retention_reason}
                                    onChange={(e) => setRetentionAction({ ...retentionAction, retention_reason: e.target.value })}
                                    required
                                    data-testid="retention-reason-input"
                                />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="retention-review-submit-button">
                                    Save retention decision
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
